import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Checkbox,
	CircularProgress,
	Container,
	FormControlLabel,
	FormGroup,
	Grid,
	Link,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from "@mui/material";
import {ApiError, countries, TemporalFormat, TextField, useFlag} from "@variocube/app-ui";
import {isPossiblePhoneNumber} from "libphonenumber-js";
import React, {
	FormEvent,
	FunctionComponent,
	PropsWithChildren,
	SVGAttributes,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {useAsyncCallback} from "react-async-hook";
import {useNavigate} from "react-router-dom";
import {displayBoxesCount, DisplayUnitsCount} from "../../booking-utils";
import {OrderSubmitError} from "../../component/order-submit-error";
import {PhoneInput} from "../../component/phone-input";
import {OrderStep, useOrder} from "../../context/order";
import {useTenant} from "../../context/tenant";
import {B2Site} from "../../data/client.gen";
import {useSiteApi} from "../../data/sites";
import {useLocalization} from "../../i18n";
import {applyDiscountReduction, formatDiscount, getDiscountReduction, isDiscountValid} from "../../order-utils";
import {DisplayCurrency, formatInteger, isBlank, notBlank} from "../../utils";
import {OrderNav} from "./order-nav";
import {useOrderSubmit} from "./use-order-submit";

import DatesIcon from "../../assets/calendar-alt.svg";
import CheckIcon from "../../assets/check-circle.svg";
import CubesIcon from "../../assets/cubes.svg";
import EditIcon from "../../assets/edit.svg";
import LocationIcon from "../../assets/map-marker-alt.svg";
import {parseZoneDateTimeFromDate, parseZoneDateTimeFromInstant} from "../../dates-utils";

function StepSummary(
	{Icon, onEdit, children}: PropsWithChildren<
		{ Icon: FunctionComponent<SVGAttributes<HTMLOrSVGElement>>; onEdit: () => void }
	>,
) {
	const {t} = useLocalization();
	return (
		<Card>
			<CardContent>
				<Grid
					container
					alignItems="flex-start"
					spacing={2}
				>
					<Grid
						item
						xs="auto"
						sx={theme => ({
							color: theme.palette.primary.main,
						})}
					>
						<Icon style={{maxWidth: "2em", maxHeight: "2em"}} />
					</Grid>
					<Grid
						item
						xs
					>
						{children}
					</Grid>
					<Grid
						item
						xs="auto"
					>
						<Box
							textAlign="right"
							ml={2}
						>
							<Button
								onClick={onEdit}
								color="primary"
								variant="outlined"
							>
								<Box
									display="inline-block"
									mr={1}
								>
									<EditIcon style={{maxWidth: "1.2em", maxHeight: "1.2em"}} />
								</Box>
								{t("actions.edit")}
							</Button>
						</Box>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
}

function DiscountForm({tenantId, siteId}: { tenantId: string; siteId: string }) {
	const {t, language} = useLocalization();
	const {checkDiscount} = useSiteApi();
	const {order: {discount: {data, discountValid}}, setDiscount} = useOrder();

	const {error, loading, execute} = useAsyncCallback(checkDiscount);

	const [code, setCode] = useState("");
	const [submit, setSubmit, clearSubmit] = useFlag(false);

	async function handleFormSubmit(ev: FormEvent) {
		ev.preventDefault();

		let data;
		try {
			data = await execute(tenantId, siteId, code);
			setSubmit();
		}
		catch (err) {
			console.error(err);
		}

		setDiscount({
			data,
			discountValid: isDiscountValid(data),
		});
	}

	return (
		<Stack
			direction="column"
			gap={2}
		>
			<form onSubmit={handleFormSubmit} onChange={clearSubmit}>
				<Grid
					container
					justifyContent="flex-start"
					spacing={2}
				>
					<Grid
						item
						xs
					>
						<TextField
							fullWidth
							size="small"
							label={t("discount.placeholder")}
							value={code}
							onChange={setCode}
							error={error !== undefined}
							helperText={error !== undefined ? t("discount.invalid") : undefined}
						/>
					</Grid>
					<Grid
						item
						xs="auto"
					>
						{data && submit && (
							<CheckIcon
								color="limegreen"
								style={{width: "2em"}}
							/>
						)}
						{(!data || !submit) && (
							<Button
								type="submit"
								variant="contained"
								color="primary"
								disabled={loading || isBlank(code)}
								sx={{
									minWidth: "7.2em",
								}}
							>
								{loading
									? (
										<CircularProgress
											size="1.8em"
											color="secondary"
										/>
									)
									: t("discount.apply")}
							</Button>
						)}
					</Grid>
				</Grid>
			</form>
			{data && discountValid && (
				<Grid
					container
					justifyContent="space-between"
				>
					<Grid item>
						<strong>{t("discount.valid")}</strong>
					</Grid>
					<Grid item>
						<Box textAlign="right">
							<strong>{t("discount.discount")}:</strong>&nbsp;<span>
								{formatDiscount(language, data)}
							</span>
						</Box>
					</Grid>
				</Grid>
			)}
		</Stack>
	);
}

function PriceBreakdown({site}: { site: B2Site }) {
	const {t} = useLocalization();
	const {order: {discount: {data}}, price = 0} = useOrder();
	return (
		<TableContainer component={Box}>
			<Table>
				<TableBody>
					{site.hasDiscount
						&& (
							<TableRow>
								<TableCell
									component="th"
									scope="row"
								>
									{t("checkout.total")}
								</TableCell>
								<TableCell align="right">
									{site.currency} <DisplayCurrency amount={price} />
								</TableCell>
							</TableRow>
						)}
					{site.hasDiscount
						&& (
							<TableRow>
								<TableCell
									component="th"
									scope="row"
								>
									{t("discount.discount")}
								</TableCell>
								<TableCell align="right">
									{site.currency} <DisplayCurrency amount={getDiscountReduction(data, price)} />
								</TableCell>
							</TableRow>
						)}
					<TableRow>
						<TableCell
							component="th"
							scope="row"
						>
							<strong>{t("checkout.charge")}</strong>
						</TableCell>
						<TableCell align="right">
							<strong>
								{site.currency} <DisplayCurrency amount={applyDiscountReduction(data, price)} />
							</strong>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
}

function CustomerForm({site}: { site: B2Site }) {
	const {requiredBookingFields, optionalBookingFields, terms, privacyPolicy} = site;
	const {t} = useLocalization();
	const {order: {customer}, setCustomer} = useOrder();
	const formRef = useRef<HTMLFormElement>(null);

	// Validate once on mount so `customerValid` is set even on a frictionless checkout (no required fields, no
	// terms, no privacy policy). Otherwise `handleCustomerChange` is the only path that ever computes
	// `customerValid`, so a site with no interactive form elements leaves it `undefined` and the Next button
	// stays disabled forever — which is exactly the zero-friction storefront site the #941 happy path books on.
	useEffect(() => {
		if (customer.customerValid === undefined) {
			setCustomer({
				...customer,
				customerValid: validateCustomerData(customer),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleCustomerChange(key: keyof typeof customer, value: boolean | string | undefined) {
		const customerData: typeof customer = {
			...customer,
			[key]: value,
		};
		setCustomer({
			...customerData,
			customerValid: validateCustomerData(customerData),
		});
	}

	function validateCustomerData(data: typeof customer) {
		if (!!terms && !data.acceptTerms) {
			console.warn("Terms not accepted.");
			return false;
		}

		if (!!privacyPolicy && !data.acceptPrivacyPolicy) {
			console.warn("Privacy policy not accepted.");
			return false;
		}

		const hasRequiredValues = requiredBookingFields
			?.map(key => ({key, value: data[key]}))
			.reduce((valid: boolean, {value}) => valid && !!value, true);
		if ((requiredBookingFields ?? []).length > 0 && !hasRequiredValues) {
			console.warn("Requirement fields not provided.");
			return false;
		}

		if (!!data.phone && !isPossiblePhoneNumber(data.phone)) {
			console.warn("Invalid phone number provided.");
			return false;
		}

		if (!formRef.current?.checkValidity()) {
			console.warn("Failed form validity check.");
			return false;
		}

		return true;
	}

	const isPhoneValid = useMemo(() => {
		return (!customer.phone || (!!customer.phone && isPossiblePhoneNumber(customer.phone)));
	}, [customer.phone]);

	return (
		<form ref={formRef}>
			<Stack
				direction="column"
				gap={2}
			>
				{(requiredBookingFields?.includes("email") || optionalBookingFields?.includes("email")) && (
					<TextField
						fullWidth
						label={t("customer.email")}
						value={customer.email}
						type="email"
						onChange={v => handleCustomerChange("email", v)}
						required={requiredBookingFields?.includes("email")}
					/>
				)}
				{(requiredBookingFields?.includes("name") || optionalBookingFields?.includes("name")) && (
					<TextField
						fullWidth
						label={t("customer.name")}
						value={customer.name}
						onChange={v => handleCustomerChange("name", v)}
						required={requiredBookingFields?.includes("name")}
					/>
				)}
				{(requiredBookingFields?.includes("phone") || optionalBookingFields?.includes("phone")) && (
					<PhoneInput
						defaultCountry={countries.find(c => c.code.toUpperCase() === (site.country ?? "").toUpperCase())
							?.phone}
						onChange={v => handleCustomerChange("phone", v)}
						error={!isPhoneValid || (requiredBookingFields?.includes("phone") && !customer.phone)}
						helperText={!isPhoneValid ? t("customer.invalidPhoneNumber") : undefined}
					/>
				)}
				{notBlank(terms) && (
					<FormGroup row>
						<FormControlLabel
							required
							control={
								<Checkbox
									checked={customer.acceptTerms}
									onChange={ev => handleCustomerChange("acceptTerms", ev.target.checked)}
									color="primary"
								/>
							}
							label={
								<small>
									{t("checkout.termsConsent.lead")}{" "}
									<Link href={terms} target="_blank">{t("checkout.terms")}</Link>{" "}
									{t("checkout.termsConsent.tail")}
								</small>
							}
						/>
					</FormGroup>
				)}
				{notBlank(privacyPolicy) && (
					<FormGroup row>
						<FormControlLabel
							required
							control={
								<Checkbox
									checked={customer.acceptPrivacyPolicy}
									onChange={ev => handleCustomerChange("acceptPrivacyPolicy", ev.target.checked)}
									color="primary"
								/>
							}
							label={
								<small>
									{t("checkout.privacyPolicyConsent.lead")}{" "}
									<Link href={privacyPolicy} target="_blank">{t("checkout.privacyPolicy")}</Link>{" "}
									{t("checkout.privacyPolicyConsent.tail")}
								</small>
							}
						/>
					</FormGroup>
				)}
			</Stack>
		</form>
	);
}

interface OrderCheckoutProps {
	site: B2Site;
}

export function OrderCheckout({site}: OrderCheckoutProps) {
	const navigate = useNavigate();
	const {t, language} = useLocalization();
	const {tenant} = useTenant();
	const {
		initiation,
		order: {
			dates: {from, until, fromDateTime, hours = 0, unitCounts = 0, datesValid},
			boxes: {types = {}, boxesValid, pricing = {}},
			discount: {data, discountValid},
			customer: {name, phone, email, customerValid},
		},
	} = useOrder();

	const {zoneId = "UTC"} = site;

	const {loading, error, submitOrderAndPay} = useOrderSubmit();

	useEffect(() => {
		if (tenant !== undefined && !from && !until && !fromDateTime && !hours) {
			navigate(`/${tenant.id}/${site.id}/dates`, {replace: true});
		}
	}, [tenant, site, from, until, fromDateTime, hours]);

	// A submit rejected because a size sold out (the lost side of a last-box race, #1202) is not a dead end: send the
	// customer straight back to box selection, where the fresh availability fetch clamps the stale quantity and shows
	// the "no longer available" notice — instead of stranding them on a generic technical error they can't act on.
	useEffect(() => {
		if (error instanceof ApiError && error.code === "BOX_SIZE_UNAVAILABLE") {
			navigate(`/${tenant?.id}/${site.id}/boxes`);
		}
	}, [error, tenant, site]);

	const isOrderValid = useMemo(() => {
		return datesValid && boxesValid && customerValid;
	}, [datesValid, boxesValid, customerValid]);

	function editOrder(step: OrderStep | undefined) {
		if (step === undefined) {
			navigate(`/${tenant?.id}`);
			return;
		}
		navigate(`/${tenant?.id}/${site.id}/${step}`);
	}

	async function handleOrderSubmit() {
		await submitOrderAndPay(site.id, {
			from,
			until,
			fromDateTime,
			hours,
			boxes: types,
			discountCode: discountValid && notBlank(data?.code) ? data?.code : undefined,
			name,
			phone,
			email,
			locale: language,
			initiationCubeId: initiation?.cubeId,
			initiationSlug: initiation?.slug,
		});
	}

	return (
		<Container maxWidth="lg">
			<Grid
				container
				alignItems="stretch"
				justifyContent="space-around"
				spacing={2}
			>
				<Grid
					item
					xs={12}
					sm={12}
					md={6}
				>
					<Stack
						direction="column"
						gap={3}
					>
						<StepSummary
							Icon={LocationIcon}
							onEdit={() => editOrder(undefined)}
						>
							<Typography
								variant="h2"
								sx={{m: 0}}
							>
								{site.name}
							</Typography>
							<Typography>
								{site.street}
								<br />
								{site.city}, {site.country}
							</Typography>
						</StepSummary>
						{datesValid && (
							<StepSummary
								Icon={DatesIcon}
								onEdit={() => editOrder("dates")}
							>
								{notBlank(from) && notBlank(until) && (
									<Grid
										container
										{...(unitCounts > 1 ? {} : {alignItems: "baseline"})}
										spacing={2}
									>
										<Grid
											item
										>
											<Typography variant="h2" sx={{m: 0}}>
												<DisplayUnitsCount unit="Days" count={unitCounts} />
											</Typography>
										</Grid>
										<Grid item>
											{unitCounts === 1
												? (
													<TemporalFormat
														value={parseZoneDateTimeFromDate(from as string, zoneId)
															.toPlainDate()}
													/>
												)
												: (
													<>
														{t("dates.from.placeholder")}{" "}
														<TemporalFormat
															value={parseZoneDateTimeFromDate(from as string, zoneId)
																.toPlainDate()}
														/>{" "}
														{t("dates.until.placeholder")}{" "}
														<TemporalFormat
															value={parseZoneDateTimeFromDate(until as string, zoneId)
																.toPlainDate()}
														/>
													</>
												)}
										</Grid>
									</Grid>
								)}
								{notBlank(fromDateTime) && hours > 0 && (
									<Grid
										container
										spacing={2}
									>
										<Grid
											item
										>
											<Typography variant="h2" sx={{m: 0}}>
												<DisplayUnitsCount unit="Hours" count={unitCounts} />
											</Typography>
										</Grid>
										<Grid item>
											{t("dates.from.placeholder")}{" "}
											<TemporalFormat
												value={parseZoneDateTimeFromInstant(fromDateTime as string, zoneId)}
											/>
										</Grid>
									</Grid>
								)}
							</StepSummary>
						)}
						{boxesValid && (
							<StepSummary
								Icon={CubesIcon}
								onEdit={() => editOrder("boxes")}
							>
								{Object.entries(types)
									.filter(([, quantity]) => quantity)
									.map(([size, quantity]) => ({
										size,
										quantity,
										box: site.boxes.find(({type}) => type === size),
									}))
									.map(({size, quantity, box}) => (
										<Box
											key={size}
											lineHeight="2.4em"
										>
											<Grid
												container
												alignItems="center"
												justifyContent="space-between"
												spacing={2}
											>
												<Grid item>
													<Grid
														container
														alignItems="baseline"
														spacing={2}
													>
														<Grid
															item
														>
															<Typography variant="h2" color="primary" sx={{m: 0}}>
																{formatInteger(quantity)}x
															</Typography>
														</Grid>
														<Grid
															item
														>
															<Typography color="primary">
																{displayBoxesCount(quantity)} <strong>{size}</strong>
															</Typography>
														</Grid>
													</Grid>
												</Grid>
												<Grid item>
													{box !== undefined && (
														<strong>
															{site.currency}{" "}
															<DisplayCurrency
																amount={quantity * (pricing[box.type] ?? 0)}
															/>
														</strong>
													)}
												</Grid>
											</Grid>
										</Box>
									))}
							</StepSummary>
						)}
					</Stack>
				</Grid>
				<Grid
					item
					xs={12}
					sm={12}
					md={6}
				>
					<Card>
						<CardHeader title={t("checkout.paymentOrder")} />
						<CardContent>
							<Stack
								direction="column"
								gap={2}
							>
								{site.hasDiscount
									&& (
										<Box
											p={2}
										>
											<DiscountForm tenantId={tenant?.id ?? ""} siteId={site.id} />
										</Box>
									)}
								<PriceBreakdown site={site} />
								<Box
									p={2}
								>
									<CustomerForm site={site} />
								</Box>
							</Stack>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			{error && (
				<Box mt={2}>
					<OrderSubmitError error={error} />
				</Box>
			)}
			<OrderNav
				site={site}
				onBack={() => navigate(`/${tenant?.id}/${site.id}/boxes`)}
				onNext={handleOrderSubmit}
				disabled={!isOrderValid || loading}
			/>
		</Container>
	);
}
